<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <ConfirmModal
    id="approve_creatives_for_specific_inventory_confirmation_modal"
    ref="confirmModal"
    size="lg"
    :primary-button-label="capitalize(i18n.t('actions.approve'))"
    :secondary-button-label="capitalize(i18n.t('actions.cancel'))"
    @cancel="reset">
    <template #modal-after-text>
      <div class="pre-select-targeting">
        <span>{{ capitalize(i18n.t('messages.preselectInventoryTargeting')) }}</span>
        <HSButton
          :label="capitalize(i18n.t('actions.preSelect'))"
          :disabled="disablePrefillButton"
          class-name="btn-sm"
          @click="prefillWithCreativeTargeting"></HSButton>
      </div>
      <OverallMap
        v-if="creativeInternal != null"
        ref="overallMap"
        :creative="creativeInternal"
        :enable-scroll-zoom="false"
        :enable-targeting-summary="true"
        :display-unit-pack-filter="true"
        :load-all-options-for-filters="true"
        :is-from-creative-pending-approvals="true"
        @show-view-summary="true"
        @cart-changed="onOverallMapSelectionsChanged" />
      <div
        v-else
        class="loading">
        <LoaderSmall />
        <span>{{ i18n.t('states.loading') }}...</span>
      </div>
    </template>
  </ConfirmModal>
</template>

<script setup lang="ts">
  import { capitalize, Ref, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { LoaderSmall } from '@/common/components/controls';
  import ConfirmModal from '@/common/components/controls/ConfirmModal.vue';
  import HSButton from '@/common/components/ui/Button/Button.vue';
  import Creative from '@/common/models/creative/Creative';
  import InventoryTargeting from '@/common/models/InventoryTargeting';
  import { CreativeMediaOwnerApprovalService } from '@/org-app/components/creatives/approval/CreativeMediaOwnerApprovalService';
  import OverallMap from '@/org-app/components/inventory/overallMap/OverallMap.vue';
  import { mapOrgAppActions } from '@/utils/StateMapping';

  const i18n = useI18n();

  //
  // Data
  //
  const selectedInventoryForApproval: Ref<InventoryTargeting> = ref(new InventoryTargeting());

  const creativeMediaOwnerApprovalIDs = ref<number[]>([]);
  const creativeInternal = ref(null);
  const confirmModal = ref<InstanceType<typeof ConfirmModal>>();
  const disablePrefillButton = ref(false);

  const { createNewCreative, getCreativeMediaOwnerApprovalInventoryTargeting } = mapOrgAppActions();

  function onOverallMapSelectionsChanged(creative: Creative) {
    const inventoryTargetingAPI = creative.toApi().inventory_targeting;
    selectedInventoryForApproval.value = InventoryTargeting.fromAPI(inventoryTargetingAPI);
    disablePrefillButton.value = false;
  }

  async function approveForSpecificInventory(mediaOwnerApprovalIDs: number[]): Promise<boolean> {
    creativeMediaOwnerApprovalIDs.value = mediaOwnerApprovalIDs;
    creativeInternal.value = null;

    // If only one media owner approval is selected, we apply the inventory targeting from that approval
    if (mediaOwnerApprovalIDs.length === 1) {
      const [id] = mediaOwnerApprovalIDs;
      const { inventoryTargeting } = await getCreativeMediaOwnerApprovalInventoryTargeting({
        id,
        showLoader: true,
      });
      selectedInventoryForApproval.value = inventoryTargeting;
    } else {
      selectedInventoryForApproval.value = new InventoryTargeting();
    }
    const creative = await createNewCreative();
    copyInventoryTargeting(creative, selectedInventoryForApproval.value);
    creativeInternal.value = creative;

    const confirmation = await confirmModal.value.confirm({
      title: i18n.t('actions.approveCreatives'),
      message: i18n.t('messages.approveCreativesForSpecificInventoryDescription'),
    });

    if (!confirmation) {
      reset();
      return false;
    }

    const targeting = selectedInventoryForApproval.value;
    reset();
    return CreativeMediaOwnerApprovalService.approveCreativeMediaApprovals(mediaOwnerApprovalIDs, targeting);
  }

  function copyInventoryTargeting(creative: Creative, inventory: InventoryTargeting) {
    creative.locationIDs = inventory?.locationIDs;
    creative.locationExclusionIDs = inventory?.locationExclusionIDs;
    creative.geofenceGroupIDs = inventory?.geofenceGroupIDs;
    creative.geofenceGroupExclusionIDs = inventory?.geofenceGroupExclusionIDs;
    creative.marketIDs = inventory?.marketIDs;
    creative.marketExclusionIDs = inventory?.marketExclusionIDs;
    creative.mediaTypeIDs = inventory?.mediaTypeIDs;
    creative.mediaTypeExclusionIDs = inventory?.mediaTypeExclusionIDs;
    creative.networkIDs = inventory?.networkIDs;
    creative.networkExclusionIDs = inventory?.networkExclusionIDs;
    creative.siteIDs = inventory?.siteIDs;
    creative.siteExclusionIDs = inventory?.siteExclusionIDs;
    creative.unitIDs = inventory?.unitIDs;
    creative.unitExclusionIDs = inventory?.unitExclusionIDs;
    creative.unitPackIDs = inventory?.unitPackIDs;
    creative.unitPackExclusionIDs = inventory?.unitPackExclusionIDs;
    creative.unitTags = inventory?.unitTags;
    creative.unitTagExclusions = inventory?.unitTagExclusions;
    creative.creativeFormats = inventory?.creativeFormats;
    creative.creativeFormatExclusions = inventory?.creativeFormatExclusions;
    creative.creativeOrientations = inventory?.creativeOrientations;
    creative.creativeOrientationExclusions = inventory?.creativeOrientationExclusions;
    return creative;
  }

  async function prefillWithCreativeTargeting() {
    creativeInternal.value = null;
    const targeting = await CreativeMediaOwnerApprovalService.getCreativesInventoryTargetingUnion(
      creativeMediaOwnerApprovalIDs.value,
    );
    selectedInventoryForApproval.value = targeting;
    const creative = await createNewCreative();
    copyInventoryTargeting(creative, selectedInventoryForApproval.value);
    creativeInternal.value = creative;
    disablePrefillButton.value = true;
  }

  function reset() {
    selectedInventoryForApproval.value = new InventoryTargeting();
    creativeInternal.value = null;
    disablePrefillButton.value = false;
  }

  defineExpose({
    approveForSpecificInventory,
  });
</script>

<style lang="scss">
  #app {
    #approve_creatives_for_specific_inventory_confirmation_modal {
      .pre-select-targeting {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: start;
        padding: 1rem 0;
        span {
          margin-right: 1rem;
        }
      }

      .loading {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
      }

      .modal-body {
        overflow-y: auto;
      }

      &.full-screen-map {
        transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
        position: fixed;
        z-index: 1;
        top: 6.4rem;
        right: 0;
        left: 0;
        bottom: 0;
        background: #fff;

        #exit_fullscreenmapInternal {
          display: block;
          z-index: 2;
        }

        .overview-map-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          height: auto;
        }
      }

      .overview-map-container {
        height: 60vh;
      }
    }
  }
</style>
